import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { FontFamily } from "@/types";
import { activeLine, setLineAttr } from "./setup";
import type { LineAttrs } from "./schema";

export function Toolbar({
  state,
  view,
  onOpenPicker,
}: {
  state: EditorState | null;
  view: EditorView | null;
  onOpenPicker: () => void;
}) {
  const line = state ? activeLine(state) : null;
  const attrs = (line?.node.attrs as LineAttrs | undefined) ?? null;
  const disabled = !view || !attrs;

  const apply = <K extends keyof LineAttrs>(k: K, v: LineAttrs[K]) => {
    if (!view) return;
    setLineAttr(k, v)(view.state, view.dispatch);
    view.focus();
  };

  const flags = attrs
    ? ([attrs.smallcaps && "smallcaps", attrs.upper && "upper"].filter(Boolean) as string[])
    : [];

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <UILabel className="text-xs text-muted-foreground">
          {attrs ? "Active line" : "Place cursor in a line"}
        </UILabel>
        <Button size="sm" variant="ghost" onClick={onOpenPicker} disabled={disabled}>
          + Emoji
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={attrs?.font ?? "serif"}
          onValueChange={(v) => v && apply("font", v as FontFamily)}
          disabled={disabled}
          className="flex-1"
        >
          <ToggleGroupItem value="serif" className="flex-1">
            Equity
          </ToggleGroupItem>
          <ToggleGroupItem value="mono" className="flex-1">
            Berkeley
          </ToggleGroupItem>
        </ToggleGroup>
        <Input
          type="number"
          className="w-20"
          value={attrs?.size ?? 80}
          disabled={disabled}
          onChange={(e) => apply("size", +e.target.value)}
          aria-label="Font size"
        />
      </div>

      <ToggleGroup
        type="multiple"
        size="sm"
        variant="outline"
        value={flags}
        onValueChange={(v: string[]) => {
          apply("smallcaps", v.includes("smallcaps"));
          apply("upper", v.includes("upper"));
        }}
        disabled={disabled}
        className="justify-start"
      >
        <ToggleGroupItem value="smallcaps" className="font-variant-caps-small-caps tracking-wide">
          Sᴄ
        </ToggleGroupItem>
        <ToggleGroupItem value="upper" className="uppercase tracking-wide">
          Aa
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}
